'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'

export default function ThreadModeration({ 
  threadId, 
  isLocked, 
  isPinned, 
  categoryId 
}: { 
  threadId: string, 
  isLocked: boolean, 
  isPinned: boolean, 
  categoryId: string 
}) {
  const [loading, setLoading] = useState<string | null>(null)
  const router = useRouter()

  const handleAction = async (action: 'pin' | 'lock' | 'delete') => {
    if (action === 'delete' && !confirm('Are you sure you want to permanently delete this thread?')) {
      return
    }

    setLoading(action)

    try {
      let res

      if (action === 'pin') {
        res = await fetch(`/api/threads/${threadId}/pin`, { method: 'PATCH' })
      } else if (action === 'lock') {
        res = await fetch(`/api/threads/${threadId}/lock`, { method: 'PATCH' })
      } else if (action === 'delete') {
        res = await fetch(`/api/threads/${threadId}`, { method: 'DELETE' })
      }

      if (res && !res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Action failed')
      }

      if (action === 'delete') {
        router.push(`/category/${categoryId}`)
      } else {
        router.refresh()
      }
    } catch (err: any) {
      alert(err.message || 'Something went wrong')
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <span className="text-xs font-black uppercase tracking-wider text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-3 py-1.5 rounded-xl shadow-inner">
          🛡️ Mod Toolbar
        </span>
      </div>

      <div className="flex items-center gap-2.5 flex-wrap">
        {/* Pin Button */}
        <button
          onClick={() => handleAction('pin')}
          disabled={loading !== null}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-md ${
            isPinned 
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30' 
              : 'bg-slate-950 text-slate-300 border border-slate-800 hover:border-slate-700 hover:text-white'
          }`}
        >
          <span>{isPinned ? '📌 Unpin Thread' : '📌 Pin Thread'}</span>
          {loading === 'pin' && <span className="animate-spin text-[10px]">⌛</span>}
        </button>

        {/* Lock Button */}
        <button
          onClick={() => handleAction('lock')}
          disabled={loading !== null}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-md ${
            isLocked 
              ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40 hover:bg-rose-500/30' 
              : 'bg-slate-950 text-slate-300 border border-slate-800 hover:border-slate-700 hover:text-white'
          }`}
        >
          <span>{isLocked ? '🔓 Unlock Thread' : '🔒 Lock Thread'}</span>
          {loading === 'lock' && <span className="animate-spin text-[10px]">⌛</span>}
        </button>

        {/* Delete Button */}
        <button
          onClick={() => handleAction('delete')}
          disabled={loading !== null}
          className="px-4 py-2 rounded-xl text-xs font-bold bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:bg-rose-500/20 transition-all flex items-center gap-1.5 shadow-md"
        >
          <span>🗑️ Delete Thread</span>
          {loading === 'delete' && <span className="animate-spin text-[10px]">⌛</span>}
        </button>
      </div>
    </div>
  )
}